import { FieldRenderProps, FieldWrapper } from '@progress/kendo-react-form'
import { DropDownList, DropDownListCloseEvent, DropDownListFocusEvent, DropDownListOpenEvent } from '@progress/kendo-react-dropdowns'
import { Label, Error, Hint } from '@progress/kendo-react-labels'
import { useRef, useState } from 'react'
const FormDropDownList = (fieldRenderProps: FieldRenderProps) => {
    const {
        validationMessage,
        touched,
        label,
        id,
        valid,
        disabled,
        hint,
        wrapperStyle,
        required,
        value,
        valueObject,
        inputPrefix,
        inputPrefixStyle,
        inputSuffix,
        wrapperClassName,
        onFocus: externalOnFocus,
        onOpen: externalOnOpen,
        onClose: externalOnClose,
        ...others
    } = fieldRenderProps

    const [opened, setOpened] = useState<boolean>(false)
    const mousePressedRef = useRef<boolean>(false)

    // A mouse press focuses the control and then Kendo toggles the popup on click, so opening on focus here would be
    // immediately undone and the user would need a second click. Only open on focus for non-mouse focus (tabbing).
    // The focus event fires synchronously after mousedown, so the flag can be cleared on the next tick.
    const handleMouseDown = () => {
        mousePressedRef.current = true
        setTimeout(() => { mousePressedRef.current = false }, 0)
    }

    // others may still carry Kendo Form's internal onFocus (visited-tracking only, does not open the popup), so
    // externalOnFocus/onOpen/onClose are extracted above and composed with our own opening logic here.
    const handleFocus = (event: DropDownListFocusEvent) => {
        if (!disabled && !mousePressedRef.current)
            setOpened(true)
        externalOnFocus?.() // Kendo Form's onFocus (visited-tracking) takes no arguments
    }

    const handleOpen = (event: DropDownListOpenEvent) => {
        setOpened(true)
        externalOnOpen?.(event)
    }

    const handleClose = (event: DropDownListCloseEvent) => {
        setOpened(false)
        externalOnClose?.(event)
    }

    const showValidationMessage = touched && validationMessage
    const showHint: boolean = !showValidationMessage && hint
    const hintId = showHint ? `${id}_hint` : ''
    const errorId = showValidationMessage ? `${id}_error` : ''
    const labelId = label ? `${id}_label` : ''

    return (
        <FieldWrapper style={wrapperStyle} className={wrapperClassName}>
            {label && <Label
                id={labelId}
                editorId={id}
                editorValid={valid}
                editorDisabled={disabled}
            >
                {required &&
                    <span className='text-danger'>*</span>
                }
                {label} &nbsp;
                {
                    showValidationMessage &&
                    <Error id={errorId}>{validationMessage}</Error>
                }
            </Label>}


            {!inputPrefix && !inputSuffix
                ? <div style={{ display: 'contents' }} onMouseDownCapture={handleMouseDown}>
                    <DropDownList
                        ariaLabelledBy={labelId}
                        ariaDescribedBy={`${hintId} ${errorId}`}
                        valid={valid}
                        id={id}
                        disabled={disabled}
                        value={valueObject ? valueObject : value}
                        {...others}
                        opened={opened}
                        onFocus={handleFocus}
                        onOpen={handleOpen}
                        onClose={handleClose}
                    />
                </div>
                : <div className='input-group d-flex'>
                    {inputPrefix
                        &&
                        <div className={'input-group-prepend'} style={inputPrefixStyle}>
                            <span className='input-group-text' style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}>{inputPrefix}</span>
                        </div>
                    }
                    <div className='flex-grow-1' style={{ display: 'flex', alignItems: 'center' }} onMouseDownCapture={handleMouseDown}>
                        <DropDownList
                            ariaLabelledBy={labelId}
                            ariaDescribedBy={`${hintId} ${errorId}`}
                            valid={valid}
                            id={id}
                            disabled={disabled}
                            value={valueObject ? valueObject : value}
                            style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                            {...others}
                            opened={opened}
                            onFocus={handleFocus}
                            onOpen={handleOpen}
                            onClose={handleClose}
                        />
                    </div>
                    {inputSuffix
                        &&
                        <div className='input-group-append'>
                            <span className='input-group-text' id='basic-addon2'>
                                {inputSuffix}
                            </span>
                        </div>
                    }
                </div>
            }
            {showHint && <Hint id={hintId}>{hint}</Hint>}
        </FieldWrapper>
    )
}

export default FormDropDownList